// src/services/api.js
// Axios instance for cookie-based (BFF) auth. The browser sends the HttpOnly session cookie
// automatically (withCredentials); there is no bearer token in JS. Writes carry the custom
// X-Axaris-Client header for the backend CSRF gate.
import axios from "axios";
import { API_BASE_URL, AUTH_BASE_URL, CLIENT_HEADER } from "../config";

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Send the browser through the backend OIDC sign-in, preserving where the user was.
function redirectToLogin() {
  if (window.__redirectingToLogin) return;
  window.__redirectingToLogin = true;
  const returnUrl = window.location.href;
  // Do NOT clear app storage here. The OIDC re-auth is a full round-trip and AuthContext re-bootstraps
  // (user/roles/hierarchy) via /Auth/validate on return, so clearing is unnecessary — and wiping
  // selectedOrganization/selectedTenantId breaks deep links (the page we return to renders with no org
  // context → blank). Clearing mid-redirect also re-rendered live components against empty storage.
  // A deliberate sign-out still clears everything (Logout.js).
  window.location.href = `${AUTH_BASE_URL}/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`;
}

// Request interceptor — attach the CSRF client header + org/tenant context. Auth is the cookie.
api.interceptors.request.use(
  (config) => {
    config.headers[CLIENT_HEADER] = "1";

    const selectedOrg = localStorage.getItem("selectedOrganization");
    if (selectedOrg) {
      try {
        const org = JSON.parse(selectedOrg);
        if (org.organizationId) {
          config.headers["Organization-Id"] = org.organizationId;
        }
      } catch (e) {
        // Invalid JSON, ignore
      }
    }

    const selectedTenantId = localStorage.getItem("selectedTenantId");
    if (selectedTenantId) {
      config.headers["Tenant-Id"] = selectedTenantId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — handle 401 and 403 errors globally.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't redirect during login/invite flows - let the component handle it
    const isAuthFlow = window.location.pathname.includes('/invite') ||
                       window.location.pathname.includes('/login') ||
                       window.location.pathname.includes('/logout');

    if (error.response?.status === 401 && !isAuthFlow) {
      const errorCode = error.response?.data?.error;
      const errorMsg = (error.response?.data?.message || "").toLowerCase();

      // Authenticated, but an org-scoped endpoint was hit with no organization in context
      // (e.g. selectedOrganization was cleared on idle logout and re-auth deep-linked straight
      // back to an org page). The backend returns 401 with a message like "Organization not found
      // in session" / "Organization-Id required". This is NOT a dead session — bouncing through
      // OIDC just lands back on the same page and loops. Send the user to the org picker instead.
      // (Guard against the picker itself, where a 401 means the session really is gone.)
      if ((errorCode === "organization_required" || errorMsg.includes("organization")) &&
          !window.location.pathname.startsWith("/organizations")) {
        localStorage.removeItem("selectedOrganization");
        window.location.href = "/organizations";
        return Promise.reject(error);
      }

      // Account-level errors (Entra user not provisioned in the app) — don't bounce through
      // OIDC again (it would loop); let the Login page surface the error.
      if (errorCode === "user_not_found" || errorCode === "oid_not_found") {
        return Promise.reject(error);
      }

      // No / expired session — re-authenticate via the backend OIDC sign-in.
      redirectToLogin();
      return Promise.reject(error);
    }

    // Handle 403 errors
    if (error.response?.status === 403 && !isAuthFlow) {
      const errorCode403 = error.response?.data?.error;
      if (errorCode403 === "user_inactive") {
        redirectToLogin();
        return Promise.reject(error);
      }

      // IP restriction block — show dedicated error screen
      if (errorCode403 === "ip_restricted") {
        localStorage.removeItem("selectedOrganization");
        sessionStorage.setItem("ipBlockedMessage", error.response.data.message || "Access denied from your IP address.");
        sessionStorage.setItem("ipBlockedDetectedIp", error.response.data.detectedIp || "");
        window.location.href = "/organizations?blocked=ip";
        return Promise.reject(error);
      }
      if (error.response?.data?.message?.toLowerCase().includes("organization")) {
        // Organization access revoked — clear org and redirect
        localStorage.removeItem("selectedOrganization");
        window.location.href = "/organizations";
      } else if (!originalRequest._roleRefreshed) {
        // Permissions may have changed server-side — refresh roles via the cookie and retry once.
        originalRequest._roleRefreshed = true;
        try {
          const validateResponse = await api.get('/Auth/validate');
          if (validateResponse.status === 200) {
            const result = validateResponse.data;
            // Update stored roles so useRBAC picks up changes
            localStorage.setItem("userRoles", JSON.stringify(result.userRoles || []));
            localStorage.setItem("orgHierarchy", JSON.stringify(result.orgHierarchy || []));
            localStorage.setItem("dealHierarchy", JSON.stringify(result.dealHierarchy || []));
            localStorage.setItem("businessUnitHierarchy", JSON.stringify(result.businessUnitHierarchy || []));
            // Retry the original request with refreshed context
            return api(originalRequest);
          }
        } catch (refreshError) {
          // Role refresh on 403 failed — fall through
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
